import { lazy, type ReactNode, Suspense } from 'react';
import { Navigate } from 'react-router-dom';

import { heroVideoPoster, marketplaceHeroVideoPoster } from '@/assets/videos';
import {
  MainLoaderSkeleton,
  withSuspense,
} from '@/components/hocs/suspense/with-suspense';
import { ROUTES, toRoutePath } from '@/constants/routes';
import { BlogDetailsPageSkeleton } from '@/pages/site/blog-details-page/skeleton';
import MeetLiveAIPage from '@/pages/site/meet-live-ai';
import { retryChunkLoad } from '@/utils/chunk-error-handler';
import { isRouteAccessibleInCurrentEnvironment } from '@/utils/prelaunch-route-access';

const PosterHeroFallback = ({
  poster,
  title,
  subtitle,
  description,
}: {
  poster: string;
  title?: ReactNode;
  subtitle?: string;
  description?: string;
}) => (
  <div className="min-h-screen bg-black text-white" aria-hidden="true">
    <div className="relative h-[min(700px,100svh)] overflow-hidden bg-black md:h-[min(982px,100svh)]">
      <img
        src={poster}
        alt=""
        loading="eager"
        {...{ fetchpriority: 'high' }}
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover opacity-60"
      />
      <div className="absolute inset-0 bg-[#00000029]" />
      {title && (
        <div className="app-container relative z-10 flex h-full flex-col items-start justify-center gap-7 py-20 text-left [@media(max-height:760px)]:gap-4 [@media(max-height:760px)]:py-10 [@media(max-height:900px)]:gap-5 [@media(max-height:900px)]:py-14">
          {subtitle && (
            <p className="max-w-3xl text-xl font-medium leading-[32px] text-white md:text-[32px]">
              {subtitle}
            </p>
          )}
          <h1 className="max-w-5xl font-fraunces text-[36px] font-bold leading-[clamp(42px,7vw,110px)] text-white sm:text-[70px] md:text-[clamp(48px,6.9vw,90px)] [@media(max-height:760px)]:text-[clamp(40px,4.4vw,60px)] [@media(max-height:760px)]:leading-[clamp(46px,5vw,66px)] [@media(max-height:900px)]:text-[clamp(44px,5vw,72px)] [@media(max-height:900px)]:leading-[clamp(50px,5.8vw,78px)]">
            {title}
          </h1>
          <div className="flex w-full max-w-[306px] items-center gap-4 sm:max-w-[620px]">
            <hr className="border-white/60 flex-1 border-t" />
          </div>
          {description && (
            <p className="text-white/80 max-w-[306px] text-lg leading-[28px] sm:max-w-[650px] sm:text-2xl sm:leading-[38px] [@media(max-height:760px)]:text-base [@media(max-height:760px)]:leading-7">
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  </div>
);

const homeRouteFallback = (
  <PosterHeroFallback
    poster={heroVideoPoster}
    title={
      <>
        AI Workforce.
        <br />
        Mission Ready.
      </>
    }
    description="Autonomous agentic AI teams for specialty finance operations with full auditability across origination, underwriting and recovery."
  />
);
const videoHeroRouteFallback = (
  <PosterHeroFallback poster={marketplaceHeroVideoPoster} />
);
const contactRouteFallback = (
  <PosterHeroFallback
    poster={marketplaceHeroVideoPoster}
    title="Contact"
    description="Talk with Agentous about product questions, pricing, partnerships, or a personalized demo."
  />
);
const integrationsRouteFallback = (
  <PosterHeroFallback
    poster={marketplaceHeroVideoPoster}
    title={
      <>
        Connect The Tools
        <br />
        You Already Use
      </>
    }
    subtitle="Let Agentous Coordinate the Work"
    description="Connect Agentous to your business stack and orchestrate work across every team."
  />
);

// Lazy load public pages with chunk error handling
const HomePage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/site/home-page'))),
  homeRouteFallback
);
const OurStoryPage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/site/our-story-page'))),
  videoHeroRouteFallback
);

const LoginPage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/auth/login-page')))
);

const SignupPage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/auth/signup-page')))
);

const ChinobiSignupPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/auth/chinobi-signup-page'))
  )
);

const ContactPage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/site/contact-page'))),
  contactRouteFallback
);

const PricingPage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/site/pricing-page'))),
  null
);

const AgentSuitePricing = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () => import('../../pages/site/pricing-page/agent-suite-pricing')
    )
  ),
  null
);

const IndividualAgentsPricing = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () => import('../../pages/site/pricing-page/individual-agents-pricing')
    )
  ),
  null
);

const IntegrationsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/site/integrations-page'))
  ),
  integrationsRouteFallback
);

const AllIntegrationsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(
      () => import('../../pages/site/integrations-page/all-integrations-page')
    )
  ),
  null
);

const TermsAndConditionsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/site/terms-and-condition-page'))
  ),
  null
);

const PrivacyPolicyPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/site/privacy-policy-page'))
  ),
  null
);

const BlogPage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/site/blog-page'))),
  null
);
const BlogDetailsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/site/blog-details-page'))
  ),
  <BlogDetailsPageSkeleton />
);

const AgentsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/site/agent-marketplace'))
  ),
  videoHeroRouteFallback
);

const AgentDetailsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/site/public-agent-details-page'))
  ),
  null
);

const SuiteDetailsPage = withSuspense(
  lazy(() =>
    retryChunkLoad(() => import('../../pages/site/suite-details-page'))
  ),
  null
);

const SeeItRunPage = withSuspense(
  lazy(() => retryChunkLoad(() => import('../../pages/site/see-it-run-page'))),
  null
);

const AuthProviders = lazy(() =>
  retryChunkLoad(() => import('../../components/auth/auth-providers'))
);

const withAuthProviders = (element: ReactNode) => (
  <Suspense fallback={<MainLoaderSkeleton />}>
    <AuthProviders>{element}</AuthProviders>
  </Suspense>
);

const shellRoutePath = (route: string) => toRoutePath(route);

export const ShellRoutes = [
  {
    index: true,
    element: <HomePage />,
  },
  {
    path: shellRoutePath(ROUTES.OUR_STORY),
    element: <OurStoryPage />,
  },
  {
    path: shellRoutePath(ROUTES.MARKETPLACE),
    element: <AgentsPage />,
  },
  {
    path: shellRoutePath(ROUTES.AGENTS_DETAILS(':agentKey')),
    element: <AgentDetailsPage />,
  },
  {
    path: shellRoutePath(ROUTES.SUITE_DETAILS(':suiteKey')),
    element: <SuiteDetailsPage />,
  },
  {
    path: shellRoutePath(ROUTES.SEE_IT_RUN),
    element: <SeeItRunPage />,
  },
  {
    path: shellRoutePath(ROUTES.LOGIN),
    element: withAuthProviders(<LoginPage />),
  },
  {
    path: shellRoutePath(ROUTES.SIGNUP),
    element: withAuthProviders(<SignupPage />),
  },
  {
    path: shellRoutePath(ROUTES.CHINOBI_SIGNUP),
    element: withAuthProviders(<ChinobiSignupPage />),
  },
  {
    path: shellRoutePath(ROUTES.CONTACT),
    element: <ContactPage />,
  },
  {
    path: shellRoutePath(ROUTES.PRICING),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.PRICING) ? (
      <PricingPage />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  {
    path: shellRoutePath(ROUTES.PRICING_SUITES),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.PRICING_SUITES) ? (
      <AgentSuitePricing />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  {
    path: shellRoutePath(ROUTES.PRICING_AGENTS),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.PRICING_AGENTS) ? (
      <IndividualAgentsPricing />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  {
    path: shellRoutePath(ROUTES.INTEGRATIONS),
    element: <IntegrationsPage />,
  },
  {
    path: shellRoutePath(ROUTES.INTEGRATIONS_ALL),
    element: <AllIntegrationsPage />,
  },
  {
    path: shellRoutePath(ROUTES.TERMS),
    element: <TermsAndConditionsPage />,
  },
  {
    path: shellRoutePath(ROUTES.PRIVACY),
    element: <PrivacyPolicyPage />,
  },
  {
    path: shellRoutePath(ROUTES.BLOG),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.BLOG) ? (
      <BlogPage />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  {
    path: shellRoutePath(ROUTES.BLOG_DETAILS(':slug')),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.BLOG) ? (
      <BlogDetailsPage />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  {
    path: shellRoutePath(ROUTES.BLOG_DETAILS(':slug', undefined, 'ledger')),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.BLOG) ? (
      <BlogDetailsPage />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  {
    path: shellRoutePath(ROUTES.BLOG_DETAILS(':slug', undefined, 'inference')),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.BLOG) ? (
      <BlogDetailsPage />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  // The Ledger and The Inference both use the shared BlogPage shell.
  {
    path: shellRoutePath(ROUTES.BLOG_LEDGER),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.BLOG_LEDGER) ? (
      <BlogPage />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  {
    path: shellRoutePath(ROUTES.BLOG_INFERENCE),
    element: isRouteAccessibleInCurrentEnvironment(ROUTES.BLOG_INFERENCE) ? (
      <BlogPage />
    ) : (
      <Navigate to={ROUTES.HOME} replace />
    ),
  },
  {
    path: shellRoutePath(ROUTES.MEET_LIVE_AI),
    element: <MeetLiveAIPage />,
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.HOME} replace />,
  },
];
